import { create } from 'zustand';

const GM_TRANSPORT_KEY = 'vooxy.gmWithTransport';
const readSaved = () => { try { return localStorage.getItem(GM_TRANSPORT_KEY) === '1'; } catch { return false; } };

// Shared "GM with / without transport" switch — one setting for every GM screen, remembered per browser.
export const useGmTransport = create((set) => ({
  withTransport: readSaved(),
  setWithTransport: (v) => {
    try { localStorage.setItem(GM_TRANSPORT_KEY, v ? '1' : '0'); } catch { /* noop */ }
    set({ withTransport: v });
  },
}));

const lineValue = (item) => (Number(item.discounted_amount) > 0
  ? Number(item.discounted_amount)
  : Math.max(Number(item.quantity || 0) * Number(item.price || 0) - Number(item.item_discount_amount || 0), 0));

// Item GM — revenue is pre-GST; with transport, the charge is revenue and freightCost (default = charge) is cost.
export const itemGm = (item, cogsMap = {}, withTransport = false, freightCost = null) => {
  const qty = Number(item.quantity || 0);
  const base = lineValue(item);
  const cogsUnit = Number(item.cogs_at_sale) > 0 ? Number(item.cogs_at_sale) : (cogsMap[item.product_id] || 0);
  const charge = withTransport ? Number(item.transport_charge || 0) : 0;
  const freight = withTransport ? (freightCost ?? charge) : 0;
  const revenue = base + charge;
  const cogsTotal = cogsUnit * qty + freight;
  if (base <= 0 || cogsUnit <= 0) return { revenue, cogsTotal, gmAmount: null, gmPct: null };
  const gmAmount = revenue - cogsTotal;
  return { revenue, cogsTotal, gmAmount, gmPct: (gmAmount / revenue) * 100 };
};

// Actual freight (if entered) split across lines by line value.
const freightShares = (items, actual) => {
  if (actual == null) return items.map(() => null);
  const vals = items.map(lineValue);
  const total = vals.reduce((a, b) => a + b, 0);
  return vals.map((v) => (total > 0 ? Number(actual) * v / total : Number(actual) / (items.length || 1)));
};

// Quote GM — lines without COGS are excluded from both revenue and cost.
export const quoteGm = (items = [], cogsMap = {}, withTransport = false, actualFreight = null) => {
  const shares = freightShares(items, actualFreight);
  let rev = 0, cogs = 0, missing = 0;
  items.forEach((it, i) => {
    const g = itemGm(it, cogsMap, withTransport, shares[i]);
    if (g.gmAmount == null) { missing += 1; return; }
    rev += g.revenue;
    cogs += g.cogsTotal;
  });
  if (rev <= 0) return { rev, cogs, gmAmount: null, gmPct: null, missing };
  return { rev, cogs, gmAmount: rev - cogs, gmPct: ((rev - cogs) / rev) * 100, missing };
};

// Pick the server-computed GM % for list rows.
export const pickGmPct = (row, withTransport) => (withTransport ? row.gm_pct_wt : row.gm_pct);
